/**
 * AddSubscriberCtrl controller
 * Add/edit subscribers
 */

import {
  US_STATES,
  CA_STATES,
  GET_TIMEZONES,
  GET_LANGUAGES,
  GET_COUNTRIES
} from 'app/core/constants'
import { findIn } from 'app/helpers/entity'
import moment from 'moment'
import { geoapifyAPI } from '../../app/helpers/geoapify'

/**
 * AddSubscriberCtrl controller
 *
 * @class AddSubscriberCtrl
 * @memberof mwApp/subscribers
 * @see mwApp/subscribers
 *
 * @ngdoc controller
 * @name mwApp/subscribers.controller:AddSubscriberCtrl
 */
/* @ngInject */
export default function AddSubscriberCtrl ($uibModal, $scope, $location, storageSvc, Network, authSvc, alertSvc, subscr, extra, Subscriber) {
  var vm = this
  vm.subscr = subscr
  vm.mask = vm.subscr.accountNumber?.length === 6 ? '-999-999' : '-999-999-999'

  /**
   * Mask for ui-mask for account number
   *
   * @memberof mwApp/subscribers.controller:AddSubscriberCtrl
   * @type {Object}
   */
  vm.accountNumberUIMaskOptions = {
    maskDefinitions: {
      A: undefined // to be able to show network prefix as part of mask
    }
  }

  /**
   * Extra
   *
   * @memberof mwApp/subscribers.controller:AddSubscriberCtrl
   * @type {Object}
   */
  vm.extra = extra

  vm.isNew = subscr._isNew()
  vm.from = $location.search().package ? 'pkg' : 'scr'

  subscr.timeZone = subscr.timeZone || storageSvc.local.get('timezone')

  if (vm.isNew) {
    var search = $location.search()
    subscr.network = search.network
      ? { id: +search.network }
      : null
  }

  vm.statesUS = US_STATES
  vm.statesCanada = CA_STATES

  GET_COUNTRIES().to(vm.countries = [])
  GET_LANGUAGES().to(vm.languages = [])
  GET_TIMEZONES().to(vm.timezones = [])

  // Note: slow migrate state for BE.
  if (['US', 'CA'].indexOf(vm.subscr.country) !== -1 && /^[A-Z]{2}$/.test(vm.subscr.state)) {
    const nameCode = (vm.subscr.country === 'US' ? vm.statesUS : vm.statesCanada).find((element) => element.code === vm.subscr.state)

    if (nameCode) {
      vm.subscr.state = nameCode.name
    }
  }

  /**
   * Checkbox if username is autogenerated
   *
   * @memberof mwApp/settings.controller:AddSubscriberCtrl
   * @type {Boolean}
   */
  vm.autoUsername = false

  /**
   * Checkbox if account number is autogenerated
   *
   * @memberof mwApp/settings.controller:AddSubscriberCtrl
   * @type {Boolean}
   */
  vm.autoAccountNumber = vm.isNew

  // networks
  vm.networks = extra.networks
  if (subscr.network && subscr.network.id) {
    subscr.network = findIn(vm.networks)(subscr.network.id)
  }

  vm.passwordPolicies = extra.passwordPolicies

  vm.passwordPatern = '^.+$'
  vm.passwordPaternMessages = ''

  vm.passwordValidationMessages = {
    STRONG: 'At least 8 characters, including letters (A–Z, a–z), one digit, and one special symbol (@#$_&-*!?.)',
    DEFAULT: 'At least 6 characters; allowed: A–Z, a–z, 0–9, @#$_&-*!?.'
  }

  vm.onNetworkChanged = function () {
    if (!authSvc.hasEnabledFlag('FEATURE_PASSWORD_POLICIES_ENABLED') || !vm.subscr?.network?.id) return

    const passwordPolicy = extra.passwordPolicies.find(passwordPolicy => passwordPolicy.networkIds.includes(vm.subscr.network.id))
    vm.passwordPatern = passwordPolicy.pattern
    vm.passwordPaternMessages = vm.passwordValidationMessages[passwordPolicy.name]
  }

  vm.onNetworkChanged()

  if (vm.isNew) {
    // assign pin code to subscriber from selected package (or random, if package pin code is null)
    $scope.$watch('vm.subscr.network', function (newValue) {
      if (newValue) {
        vm.subscr.pincode = newValue.pincode ? newValue.pincode : String(Math.random() + 1).substring(2, 6)
      }
    })
  }

  // dates handling
  vm.dpCfg = {
    maxDate: new Date(),
    minDate: new Date(1900, 1, 1),
    format: 'dd.MM.yyyy',
    openedDOB: false,
    changeHandler: function (form) {
      var d = vm.subscr.$dateOfBirth ? vm.subscr.$dateOfBirth.getTime() : null
      form.pdob.$setValidity(
        'interval',
        d &&
        d >= vm.dpCfg.minDate.getTime() &&
        d <= vm.dpCfg.maxDate.getTime()
      )
    }
  }
  vm.openDOB = function () {
    vm.dpCfg.openedDOB = true
  }

  vm.asdCfg = {
    maxDate: moment().endOf('day').toDate(),
    minDate: new Date(1900, 1, 1),
    format: 'dd.MM.yyyy',
    openedASD: false,
    changeHandler: function (form) {
      form.accountStartDate.$setTouched()
      const maxError = vm.asdCfg.maxDate < vm.subscr.$tvsAccountStartDate
      form.accountStartDate.$setValidity('max', !maxError)
    }
  }
  vm.openASD = function () {
    vm.asdCfg.openedASD = true
  }

  /**
   * If subscriber's password has changed
   *
   * @memberof mwApp/settings.controller:AddSubscriberCtrl
   * @type {Boolean}
   */
  vm.passChanged = false

  /**
   * If subscriber's password is visible
   *
   * @memberof mwApp/settings.controller:AddSubscriberCtrl
   * @type {Boolean}
   */
  vm.passVisible = false

  /**
   * Subscriber's password, obtained from server
   *
   * @memberof mwApp/settings.controller:AddSubscriberCtrl
   * @type {String}
   */
  vm.storedPassword = null

  // watch changes on password field
  $scope.$watch('vm.subscr.password', function (newValue, oldValue) {
    vm.passChanged = newValue && newValue !== oldValue && newValue !== vm.storedPassword
    if (!vm.passChanged) {
      vm.passwordAgain = null
    }
  })

  /**
   * Show subscriber's password
   *
   * @memberof mwApp/settings.controller:AddSubscriberCtrl
   */
  vm.viewPassword = function () {
    vm.passVisible = !vm.passVisible
    if (vm.passVisible && !subscr.password) {
      if (vm.isNew || vm.storedPassword !== null) {
        subscr.password = vm.storedPassword
      } else {
        subscr._getPassword()
          .data()
          .then(password => { subscr.password = vm.storedPassword = password })
      }
    } else
    if (!vm.passVisible && subscr.password === vm.storedPassword) {
      subscr.password = null
    }
  }

  /**
   * Show subscriber's password
   *
   * @memberof mwApp/settings.controller:AddSubscriberCtrl
   */
  vm.viewThaiId = function () {
    vm.thaiIdVisible = !vm.thaiIdVisible
  }

  vm.validateEmail = function () {
    if (!vm.subscr.email) {
      vm.form.pemail.$invalid = true
      return
    }

    Subscriber.validateEmail(vm.subscr.email || '').then((res) => {
      vm.validEmail = res
      vm.form.pemail.$touched = true
      delete vm.form.pemail.$error.validateEmail
      if (!res) {
        vm.form.pemail.$error.incorectEmail = true
        vm.form.pemail.$invalid = true
      } else {
        delete vm.form.pemail.$error.incorectEmail
        vm.form.pemail.$invalid = false
      }
      if (vm.submitForm) {
        vm.submitForm = false
        vm.submit()
      }
    })
  }

  vm.changeEmail = function () {
    vm.validEmail = undefined
    delete vm.form.pemail.$error.incorectEmail
    vm.form.pemail.$invalid = false
  }

  /**
   * Create/update subscriber
   *
   * @memberof mwApp/subscribers.controller:AddSubscriberCtrl
   */
  vm.submit = function () {
    if (vm.validEmail === undefined) {
      vm.submitForm = true
      if (!vm.form.pemail.$touched) {
        vm.validateEmail()
      }
      return
    }
    if (vm.form.pemail.$invalid) return
    vm.setDisable()

    if (vm.dirtyPusername) {
      $scope.form.pusername.$setValidity('pattern', vm.nameRegex.test($scope.form.pusername.$modelValue))
      if (!vm.nameRegex.test($scope.form.pusername.$modelValue)) {
        vm.unsetDisable()
        return
      }
    }
    const doSave = () => subscr._save(
      subscr => {
        subscr.currentPaymentStatement = null
        subscr.zipcode = subscr.zipcode ? subscr.zipcode.toUpperCase() : null
        subscr.name = vm.autoUsername ? null : subscr.name
        subscr.accountNumber = vm.autoAccountNumber ? null : subscr.accountNumber
        subscr.password = vm.passChanged ? subscr.password : null
      }
    ).then(vm.location(subscr).reload)

    vm.validateAddress()
      .then(() => doSave())
      .finally(vm.unsetDisable)
  }

  /**
   * Delete subscriber
   *
   * @memberof mwApp/subscribers.controller:AddSubscriberCtrl
   */
  vm.delete = function () {
    vm.setDisable()

    subscr._delete()
      .then(
        () => {
          $location.url(vm.from === 'pkg' ? '/packages' : '/subscribers')
        }
      )
      .finally(vm.unsetDisable)
  }

  vm.dirtyPusername = false
  vm.nameRegex = new RegExp('^[^@]*$')
  $scope.$watch('form.pusername.$modelValue', () => {
    if (($scope.form && $scope.form.pusername && $scope.form.pusername.$dirty) || vm.dirtyPusername) {
      vm.dirtyPusername = true
      $scope.form.pusername.$setValidity('pattern', vm.nameRegex.test($scope.form.pusername.$modelValue))
    }
  })

  vm.onCountrySelect = function () {
    vm.subscr.state = ''
  }

  $scope.$watch('vm.subscr.phone', (newValue) => {
    if (newValue) {
      vm.subscr.phone = vm.isNew ? newValue.replace(/\D/g, '') : newValue.replace(/^\++/, '')
    }
  })

  /**
   * Change package for a subscriber
   *
   * @memberof mwApp/subscribers.controller:AddSubscriberCtrl
   * @function
   */
  vm.changePackage = function () {
    $uibModal.open({
      templateUrl: 'components/subscribers/views/changePackage.html',
      controller: 'ChangePackageCtrl as vm',
      animation: false,
      size: 'lg',
      resolve: {
        subscriberId: function () {
          return vm.subscr.id
        }
      }
    }).result
      .then(() => {
        Subscriber.load({ id: subscr.id })
          .then(
            (item) => {
              vm.subscr.network = item.network
            }
          )
      })
  }

  /**
   * Update mobile operator for a subscriber
   *
   * @memberof mwApp/subscribers.controller:AddSubscriberCtrl
   * @function
   */
  vm.updateOperator = function () {
    Subscriber.updateOperator(subscr.id).then((res) => {
      if (res.operator && res.operator !== subscr.operator) {
        vm.subscr.operator = res.operator
        alertSvc.alert(`Mobile Operator has been updated to ${res.operator}.`, 'success')
      } else {
        alertSvc.alert('Mobile Operator is not changed according to the response.', 'success')
      }
    }).catch(err => {
      alertSvc.alert(err.data, 'danger')
    })
  }

  vm.hasFlag = function (flag) {
    return authSvc.hasEnabledFlag(flag)
  }

  vm.disableGeneralInfo = function () {
    if (authSvc.hasAccess('superAdmin')) {
      return false
    }
    return !vm.isNew && authSvc.hasAccess('limitGeneralInfoSubscriber')
  }

  vm.validateAddress = function () {
    const street = `&street=${vm.subscr.address?.replace(/ /g, '%20') || ''}`
    const state = vm.subscr.state?.replace(/ /g, '%20')
    const stateParam = state ? `&state=${state}` : ''
    const city = `&city=${vm.subscr.city || ''}`
    const postCode = `&postcode=${vm.subscr.zipcode || ''}`
    const country = `&country=${vm.countries.find(c => c.code === vm.subscr.country)?.name || ''}`

    const searchString = postCode + country + street + city + stateParam

    return geoapifyAPI.validateAddress(searchString, alertSvc)
  }

  vm.stateChanged = function () {
    const state = vm.subscr.state?.replace(/ /g, '%20')
    if (!state) return

    return geoapifyAPI.findAddresses(state, { countryCode: vm.subscr.country, filter: 'state' })
  }

  vm.cityChanged = function () {
    const city = vm.subscr.city?.replace(/ /g, '%20')
    if (!city) return

    return geoapifyAPI.findAddresses(city, { countryCode: vm.subscr.country, filter: 'city' })
  }

  vm.addressChanged = function () {
    const street = vm.subscr.address?.replace(/ /g, '%20')
    if (!street) return

    return geoapifyAPI.findAddresses(street, { countryCode: vm.subscr.country, filter: 'street' })
  }

  vm.onAddressSelect = function (item) {
    vm.subscr.address = item.properties.street + ' ' + (item.properties.housenumber || '')

    if (item.properties.city) {
      vm.subscr.city = item.properties.city
    }

    if (item.properties.postcode) {
      vm.subscr.zipcode = item.properties.postcode
    }

    if (vm.subscr.country !== 'US' && vm.subscr.country !== 'CA') {
      vm.subscr.state = item.properties.state
    }
  }

  vm.getFormattedStreet = function (item) {
    return [item?.properties?.street, item?.properties?.housenumber].filter(Boolean).join(', ')
  }

  vm.onCitySelect = function (item) {
    vm.subscr.zipcode = item.properties.postcode

    if (vm.subscr.country !== 'US' && vm.subscr.country !== 'CA') {
      vm.subscr.state = item.properties.state
    }
  }

  vm.zipcodeChanged = function () {
    const postCode = vm.subscr.zipcode?.replace(/ /g, '%20')
    if (!postCode) return

    return geoapifyAPI.findAddresses(postCode, { countryCode: vm.subscr.country, filter: 'postcode' })
  }

  vm.getFormattedPostcode = function (item) {
    return [item?.properties?.postcode, item?.properties?.city].filter(Boolean).join(', ')
  }

  vm.onPostcodeSelect = function (item) {
    vm.subscr.zipcode = item.properties.postcode
  }

  //
  // vm.getLocations = function (text) {
  //   return window.fetch(`https://api.geoapify.com/v1/geocode/search?apiKey=${vm.APP_GEOAPIFY_API_KEY}` + text, requestOptions)
  //     .then(response => response.json())
  //     .then(result => result.features)
  //     .catch(error => console.log('error', error))
  // }
  //
  // vm.getLocation = function (searchString) {
  //   return vm.getLocations('&text=' + searchString.replace(/ /g, '%20'))
  // }
  //
  // vm.selectLocation = function (item) {
  //   if (item.properties.address_line2) {
  //     vm.subscr.address = item.properties.address_line2
  //   }
  //
  //   if (item.properties.city) {
  //     vm.subscr.city = item.properties.city
  //   }
  //
  //   if (item.properties.postcode) {
  //     vm.subscr.zipcode = item.properties.postcode
  //   }
  //
  //   if (item.properties.country_code) {
  //     vm.subscr.country = item.properties.country_code.toUpperCase()
  //   }
  //
  //   if (item.properties.state) {
  //     vm.subscr.state = item.properties.state
  //   }
  //
  //   if (item.properties.rank.confidence !== undefined) {
  //     vm.addressValidation = item.properties.rank.confidence
  //   }
  // }
  //
  // vm.addressChanged = function () {
  //   setTimeout(() => {
  //     const validators = [
  //       vm.form.paddress.$invalid,
  //       vm.form.pcity.$invalid,
  //       vm.form.pzipcode.$invalid,
  //       vm.form.pcountry.$invalid,
  //       vm.form['p-state']?.$invalid
  //     ]
  //     if (validators.includes(true)) return
  //
  //     const text = '&text=' + vm.subscr.address.replace(/ /g, '%20') + vm.subscr.state.replace(/ /g, '%20')
  //     const city = '&city=' + vm.subscr.city
  //     const postCode = `&postcode=${vm.subscr.zipcode}`
  //     const country = '&country=' + vm.countries.find(c => c.code === vm.subscr.country)?.name
  //
  //     const searchString = postCode + country + text + city
  //
  //     vm.loadingLocations = true
  //     vm.getLocations(searchString)
  //       .then(data => {
  //         if (data.length) {
  //           const item = data[0]
  //           vm.addressValidation = item.properties.rank.confidence
  //           if (vm.addressValidation > vm.addressRank) {
  //             vm.searchAddress = item.properties.formatted
  //           } else {
  //             vm.searchAddress = ''
  //             vm.addressValidation = 0
  //           }
  //         } else {
  //           vm.searchAddress = ''
  //           vm.addressValidation = 0
  //         }
  //       })
  //       .catch(err => console.log(err))
  //       .finally(() => {
  //         vm.form.searchAddress.$setTouched()
  //         vm.loadingLocations = false
  //       })
  //   }, 0)
  // }
}
